import {
  BarChartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  App as AntdApp,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FeedbackFormDetails from '../components/FeedbackFormDetails';
import FeedbackFormModal from '../components/FeedbackFormModal';
import api from '../config/api';

const { Option } = Select;

interface FeedbackForm {
  id: string;
  title: string;
  description?: string;
  eventId: string;
  event: {
    id: string;
    title: string;
    startDate: string;
    endDate: string;
  };
  isActive: boolean;
  openAt?: string;
  closeAt?: string;
  _count: {
    questions: number;
    responses: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface Event {
  id: string;
  title: string;
}

const FeedbackForms: React.FC = () => {
  const { t } = useTranslation();
  // Context-tudatos megerősítő, hogy a panel témája elérje (#145) — lásd a
  // Regisztrációk oldal megjegyzését.
  const { modal } = AntdApp.useApp();
  const [forms, setForms] = useState<FeedbackForm[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedForm, setSelectedForm] = useState<FeedbackForm | null>(null);
  const [editingForm, setEditingForm] = useState<FeedbackForm | null>(null);
  const [statistics, setStatistics] = useState({
    totalForms: 0,
    activeForms: 0,
    totalResponses: 0,
    averageResponseRate: 0,
  });

  const fetchForms = async () => {
    setLoading(true);
    try {
      const params = selectedEvent !== 'all' ? { eventId: selectedEvent } : {};
      const response = await api.get('/feedback/forms', { params });
      setForms(response.data.data || response.data);

      // Calculate statistics
      const formsData = response.data.data || response.data;
      const totalForms = formsData.length;
      const activeForms = formsData.filter((f: FeedbackForm) => f.isActive).length;
      const totalResponses = formsData.reduce(
        (sum: number, f: FeedbackForm) => sum + f._count.responses,
        0
      );
      const averageResponseRate =
        totalForms > 0
          ? formsData.reduce((sum: number, f: FeedbackForm) => sum + f._count.responses, 0) /
            totalForms
          : 0;

      setStatistics({
        totalForms,
        activeForms,
        totalResponses,
        averageResponseRate: Math.round(averageResponseRate * 10) / 10,
      });
    } catch (error) {
      message.error(t('feedbackForms.formsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  useEffect(() => {
    fetchForms();
    fetchEvents();
  }, [selectedEvent]);

  const handleCreate = () => {
    setEditingForm(null);
    setModalVisible(true);
  };

  const handleEdit = (form: FeedbackForm) => {
    setEditingForm(form);
    setModalVisible(true);
  };

  const handleView = (form: FeedbackForm) => {
    setSelectedForm(form);
    setDetailsVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/feedback/forms/${id}`);
      message.success(t('feedbackForms.deleteSuccess'));
      fetchForms();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('feedbackForms.deleteFailed'));
    }
  };

  const handleClone = async (form: FeedbackForm) => {
    modal.confirm({
      title: t('feedbackForms.cloneTitle'),
      content: (
        <div>
          <p>{t('feedbackForms.cloneSelectPrompt')}</p>
          <Select
            style={{ width: '100%', marginTop: 10 }}
            placeholder={t('feedbackForms.cloneSelectPlaceholder')}
            id="clone-event-select"
          >
            {events.map((event) => (
              <Option key={event.id} value={event.id}>
                {event.title}
              </Option>
            ))}
          </Select>
        </div>
      ),
      onOk: async () => {
        const selectElement = document.getElementById('clone-event-select') as any;
        const targetEventId = selectElement?.value;

        if (!targetEventId) {
          message.error(t('feedbackForms.cloneTargetRequired'));
          return;
        }

        try {
          await api.post(`/feedback/forms/${form.id}/clone`, {
            targetEventId,
            // A másolat CÍME adat: az adatbázisba íródik. A mérce nem az, hogy
            // van-e hozzá résztvevői felület, hanem hogy a tárolt szöveget
            // olyan közönség olvassa-e vissza, akinek a nyelvét MÁS dönti el,
            // mint aki beírta. Itt ugyanaz a szervező olvassa vissza, ugyanabban
            // a panelben — a fordítás tehát nem lenne hiba. Bedrótozva mégis
            // jobb: így a tárolt adat egyáltalán nem hordoz felületi nyelvet, és
            // aki angolul klónoz, majd magyarra vált, nem talál „Másolat"-ot és
            // „(Copy)"-t vegyesen a listájában. (A szponzorszinteknél a helyzet
            // más és szigorúbb: ott az `Event.language` dönt a közönségről.)
            title: `${form.title} (Copy)`,
          });
          message.success(t('feedbackForms.cloneSuccess'));
          fetchForms();
        } catch (error) {
          message.error(t('feedbackForms.cloneFailed'));
        }
      },
    });
  };

  const handleExport = async (form: FeedbackForm, format: 'csv' | 'json') => {
    try {
      const response = await api.get(`/feedback/forms/${form.id}/export`, {
        params: { format },
        responseType: format === 'csv' ? 'text' : 'json',
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `feedback-${form.id}-${dayjs().format('YYYY-MM-DD')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: 'application/json',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `feedback-${form.id}-${dayjs().format('YYYY-MM-DD')}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      message.success(t('feedbackForms.exportSuccess', { format: format.toUpperCase() }));
    } catch (error) {
      message.error(t('feedbackForms.exportFailed'));
    }
  };

  const handleToggleActive = async (form: FeedbackForm) => {
    try {
      await api.patch(`/feedback/forms/${form.id}`, {
        isActive: !form.isActive,
      });
      message.success(
        form.isActive
          ? t('feedbackForms.toggleSuccessDeactivated')
          : t('feedbackForms.toggleSuccessActivated')
      );
      fetchForms();
    } catch (error) {
      message.error(t('feedbackForms.toggleFailed'));
    }
  };

  const columns: ColumnsType<FeedbackForm> = [
    {
      title: t('feedbackForms.columnTitle'),
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space>
          {/* Link-kinézetű gomb a csupasz `<a onClick>` helyett: billentyűzettel
              is nyitható, és az élő admin Biome-kapu enélkül minden, ezt a
              fájlt érintő commitot elbuktatott (#145). */}
          <Button
            type="link"
            style={{ padding: 0, height: 'auto' }}
            onClick={() => handleView(record)}
          >
            {text}
          </Button>
          {record.isActive && <Badge status="success" text={t('feedbackForms.badgeActive')} />}
        </Space>
      ),
    },
    {
      title: t('feedbackForms.columnEvent'),
      dataIndex: ['event', 'title'],
      key: 'event',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: t('feedbackForms.columnQuestions'),
      dataIndex: ['_count', 'questions'],
      key: 'questions',
      align: 'center',
      render: (count) => <Badge count={count} showZero style={{ backgroundColor: '#52c41a' }} />,
    },
    {
      title: t('feedbackForms.columnResponses'),
      dataIndex: ['_count', 'responses'],
      key: 'responses',
      align: 'center',
      render: (count) => <Badge count={count} showZero style={{ backgroundColor: '#1890ff' }} />,
    },
    {
      title: t('feedbackForms.columnStatus'),
      key: 'status',
      render: (_, record) => {
        const now = dayjs();
        const openAt = record.openAt ? dayjs(record.openAt) : null;
        const closeAt = record.closeAt ? dayjs(record.closeAt) : null;

        if (!record.isActive) {
          return <Tag color="default">{t('feedbackForms.statusInactive')}</Tag>;
        }

        if (openAt && now.isBefore(openAt)) {
          return (
            <Tooltip
              title={t('feedbackForms.opensAt', {
                date: openAt.format('YYYY-MM-DD HH:mm'),
              })}
            >
              <Tag color="warning" icon={<ClockCircleOutlined />}>
                {t('feedbackForms.statusScheduled')}
              </Tag>
            </Tooltip>
          );
        }

        if (closeAt && now.isAfter(closeAt)) {
          return <Tag color="default">{t('feedbackForms.statusClosed')}</Tag>;
        }

        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t('feedbackForms.statusActive')}
          </Tag>
        );
      },
    },
    {
      title: t('feedbackForms.columnCreated'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: t('feedbackForms.columnActions'),
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('feedbackForms.actionView')}>
            <Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title={t('feedbackForms.actionEdit')}>
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title={t('feedbackForms.actionClone')}>
            <Button type="link" icon={<CopyOutlined />} onClick={() => handleClone(record)} />
          </Tooltip>
          <Tooltip title={t('feedbackForms.actionAnalytics')}>
            <Button
              type="link"
              icon={<BarChartOutlined />}
              onClick={() => (window.location.href = `/feedback/analytics/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('feedbackForms.actionExport')}>
            <Button.Group>
              <Button
                type="link"
                icon={<ExportOutlined />}
                onClick={() => handleExport(record, 'csv')}
              />
            </Button.Group>
          </Tooltip>
          <Tooltip
            title={record.isActive ? t('feedbackForms.deactivate') : t('feedbackForms.activate')}
          >
            <Button type="link" danger={record.isActive} onClick={() => handleToggleActive(record)}>
              {record.isActive ? t('feedbackForms.deactivate') : t('feedbackForms.activate')}
            </Button>
          </Tooltip>
          {record._count.responses === 0 && (
            <Popconfirm
              title={t('feedbackForms.deleteConfirmTitle')}
              description={t('feedbackForms.deleteConfirmBody')}
              onConfirm={() => handleDelete(record.id)}
              okText={t('feedbackForms.yes')}
              cancelText={t('feedbackForms.no')}
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('feedbackForms.statTotalForms')}
              value={statistics.totalForms}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('feedbackForms.statActiveForms')}
              value={statistics.activeForms}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('feedbackForms.statTotalResponses')}
              value={statistics.totalResponses}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('feedbackForms.statAvgResponses')}
              value={statistics.averageResponseRate}
              precision={1}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={t('feedbackForms.cardTitle')}
        extra={
          <Space>
            <Select
              style={{ width: 200 }}
              placeholder={t('feedbackForms.filterByEvent')}
              value={selectedEvent}
              onChange={setSelectedEvent}
            >
              <Option value="all">{t('feedbackForms.allEvents')}</Option>
              {events.map((event) => (
                <Option key={event.id} value={event.id}>
                  {event.title}
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('feedbackForms.newForm')}
            </Button>
          </Space>
        }
      >
        {forms.length === 0 && !loading ? (
          <Empty description={t('feedbackForms.emptyText')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" onClick={handleCreate}>
              {t('feedbackForms.createFirst')}
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={forms}
            rowKey="id"
            loading={loading}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => t('feedbackForms.totalForms', { count: total }),
            }}
          />
        )}
      </Card>

      <FeedbackFormModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={() => {
          setModalVisible(false);
          fetchForms();
        }}
        form={editingForm}
        events={events}
      />

      <FeedbackFormDetails
        visible={detailsVisible}
        onClose={() => setDetailsVisible(false)}
        form={selectedForm}
        onRefresh={fetchForms}
      />
    </div>
  );
};

export default FeedbackForms;
